import type { MedicationStatementModel } from '@ctw/shared/api/fhir/models/medication-statement';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useFilteredSortedData } from '@ctw/shared/hooks/use-filtered-sorted-data';
import { useHotloadReady } from '@ctw/shared/hooks/use-hotload-ready';
import { useQueryAllPatientMedications } from '@ctw/shared/hooks/use-medications';
import { getDateRangeView } from '../resource/view-date-range';
import { defaultMedicationSortOverview } from './helpers/sorts';

export interface PatientMedicationsOverviewData {
  data: Array<MedicationStatementModel>;
  isLoading: boolean;
  hasNoRecords: boolean;
}

/**
 * Fetches, filters (past 6 months), and sorts the medications shown in the
 * patient overview card. `hasNoRecords` reflects the raw fetch so callers can
 * distinguish "no medications" from "none within the past 6 months".
 */
export function usePatientMedicationsOverviewData(): PatientMedicationsOverviewData {
  const { patient } = usePatientContext();
  const ready = useHotloadReady({ upid: patient.UPID, resourceTypes: ['MedicationStatement'] });
  const query = useQueryAllPatientMedications(ready);

  const { past6Months } = getDateRangeView<MedicationStatementModel>('lastActivityDate', false);

  const { data } = useFilteredSortedData({
    defaultSort: defaultMedicationSortOverview, // Alphabetical
    viewOptions: [past6Months],
    defaultView: past6Months.display,
    records: query.allMedications,
  });

  return {
    data,
    isLoading: !ready || query.isLoading,
    hasNoRecords: query.allMedications.length === 0,
  };
}
